import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ASSETS, ARC } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";
import InteractiveTerminal from "@/components/Terminal";
import { Particles, GridBackground, DataStream } from "@/components/Effects";

function useSessionClock() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return `00:${mm}:${ss}`;
}

export default function TerminalPage() {
  const { t } = useLanguage();
  const session = useSessionClock();

  const modules = [
    t("terminalPage.coreSystem"),
    t("terminalPage.arcConnection"),
    t("terminalPage.walletModule"),
    t("terminalPage.marketModule"),
    t("terminalPage.agentEngine"),
  ];

  const statusRows = [
    [t("terminalPage.status"), t("terminalPage.online"), true],
    [t("terminalPage.network"), ARC.networkLabel, false],
    [t("terminalPage.chain"), String(ARC.chainId), false],
    [t("terminalPage.mode"), t("terminalPage.autonomous"), false],
    [t("terminalPage.session"), session, false],
  ];

  return (
    <div data-testid="terminal-page" className="relative min-h-screen overflow-hidden">
      <GridBackground />
      <Particles count={36} />
      <DataStream left="12%" delay={0} duration={11} />
      <DataStream left="86%" delay={3} duration={14} />

      <PageHeader
        label={t("terminalPage.label")}
        title={t("terminalPage.title")}
        sub={t("terminalPage.sub")}
      />

      <div className="relative mx-auto grid max-w-7xl gap-6 px-4 pb-24 pt-10 sm:px-6 lg:grid-cols-[280px_1fr_260px]">
        {/* LEFT — agent art */}
        <motion.aside
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="hidden lg:block"
        >
          <div className="mizu-corners relative overflow-hidden border border-mizu-cyan/20">
            <img
              src={ASSETS.art}
              alt="MIZUKARA"
              data-testid="terminal-agent-art"
              className="aspect-[3/4] w-full animate-float-slow object-cover object-[30%_15%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#020611] via-transparent to-[#020611]/40" />
            <div className="absolute inset-x-0 bottom-0 p-3">
              <div className="font-mono text-[10px] tracking-[0.25em] text-mizu-cyan">
                {t("terminalPage.agentCardTitle")}
              </div>
              <div className="mt-1 font-mono text-[9px] tracking-[0.15em] text-mizu-dim">
                {t("terminalPage.designation")}
              </div>
            </div>
            {/* scanline sweep */}
            <div className="pointer-events-none absolute inset-x-0 h-16 animate-scan-y bg-gradient-to-b from-transparent via-mizu-cyan/10 to-transparent" />
          </div>
        </motion.aside>

        {/* CENTER — interactive terminal */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
        >
          <InteractiveTerminal height="h-[520px]" />
        </motion.div>

        {/* RIGHT — agent status */}
        <motion.aside
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          className="space-y-4"
        >
          <div className="mizu-panel p-4" data-testid="terminal-status-panel">
            <div className="mb-3 border-b border-mizu-cyan/15 pb-2 font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
              {t("terminalPage.rightTitle")}
            </div>
            {statusRows.map(([k, v, live]) => (
              <div
                key={k}
                className="flex items-center justify-between py-1.5 font-mono text-[11px]"
              >
                <span className="tracking-[0.15em] text-mizu-faint">{k}</span>
                <span className={`flex items-center gap-1.5 ${live ? "text-mizu-cyan" : "text-mizu-silver"}`}>
                  {live && <span className="h-1.5 w-1.5 animate-pulse-glow bg-mizu-cyan" />}
                  {v}
                </span>
              </div>
            ))}
          </div>

          <div className="mizu-panel p-4" data-testid="terminal-modules-panel">
            <div className="mb-3 border-b border-mizu-cyan/15 pb-2 font-mono text-[10px] tracking-[0.3em] text-mizu-cyan">
              {t("terminalPage.modules")}
            </div>
            {modules.map((m, i) => (
              <motion.div
                key={m}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.12 }}
                className="py-1.5 font-mono text-[11px] text-mizu-cyan/85"
              >
                [✓] {m}
              </motion.div>
            ))}
          </div>

          <div className="mizu-panel p-4 text-center">
            <div className="font-display text-sm font-bold tracking-[0.3em] text-white">
              OBSERVE
            </div>
            <div className="font-display text-sm font-bold tracking-[0.3em] text-mizu-cyan/80">
              THINK
            </div>
            <div className="font-display text-sm font-bold tracking-[0.3em] text-white">
              EXECUTE
            </div>
          </div>
        </motion.aside>
      </div>
    </div>
  );
}
